import { API_ENDPOINT } from 'helper';
import { useQuery } from 'react-query';
import { useJwtToken } from 'helper';
import dayjs from 'dayjs';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useDevice } from 'components/DeviceDetails/service';

export const useDeviceLogs = (filterStartTime, filterEndTime, filterLevels) => {
    const { BMS_ID, Module_ID, Thing_ID } = useGetIDsFromURL();
    const { data: device } = useDevice(Thing_ID);
    const accessToken = useJwtToken();

    // EMS route has no BMS_ID — use first MASTER_SN from config
    const emsFallback = !BMS_ID && !Module_ID
        ? device?.Config?.data?.MASTER_SNS?.[0]?.MASTER_SN
        : null;
    const serialNumber = Module_ID ?? BMS_ID ?? emsFallback;

    return useQuery(
        ['deviceLogs', serialNumber, filterStartTime, filterEndTime, filterLevels],
        async () => {
            const payloadData = {
                serial_number: serialNumber,
            };

            if (filterStartTime) {
                // Convert filterStartTime to UNIX timestamp in seconds
                payloadData.start_time = Math.floor(
                    new Date(filterStartTime).getTime() / 1000
                );
            }

            if (filterEndTime) {
                // Convert filterEndTime to UNIX timestamp in seconds
                payloadData.end_time = Math.floor(
                    new Date(filterEndTime).getTime() / 1000
                );
            }

            if (filterLevels.length > 0) {
                // Add levels to the payload as an array
                payloadData.level = filterLevels;
            }
            
            const result = await getDeviceLogs(payloadData, accessToken);
            const logs = result?.items ?? [];
            if (Array.isArray(logs)) {
                logs.forEach((log) => {
                    log.start_time_formatted = dayjs(
                        log.start_time * 1000
                    ).format('YYYY MMMM D hh:mm:ss A');
                    log.end_time_formatted = log.end_time
                        ? dayjs(log.end_time * 1000).format(
                              'YYYY MMMM D hh:mm:ss A'
                          )
                        : 'Active';
                });
                return { logs, total: result.total, capped: result.capped };
            }
            return { logs: [], total: 0, capped: false };
        },
        {
            enabled: !!accessToken && !!serialNumber,
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );
};

export const getDeviceLogs = async (payloadData, accessToken) => {
    try {
        const response = await fetch(
            `${API_ENDPOINT}/devices/${payloadData.serial_number}/logs`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken,
                },
                body: JSON.stringify(payloadData),
            }
        );

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    } catch (error) {
        console.error('Failed to fetch device logs:', error.message);
        throw error;
    }
};
